import { useEffect, useState } from 'react';
import { Menu, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/atoms/Button';
import { LanguageGlobe } from '@/components/i18n/LanguageGlobe';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/molecules/InputGroup';

interface TopbarProps {
  onOpenMobileNav?: () => void;
  onOpenSearch?: () => void;
}

const detectIsMac = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
};

const kbdClass = 'inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 rounded border border-light-lila/60 bg-light text-[10px] font-semibold text-dark-invirtus/55 font-body';

export const Topbar = ({ onOpenMobileNav, onOpenSearch }: TopbarProps) => {
  const { t } = useTranslation('common');
  const [isMac, setIsMac] = useState(false);
  useEffect(() => {
    setIsMac(detectIsMac());
  }, []);

  const openSearch = () => onOpenSearch?.();

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-light-lila/40 bg-light px-3 md:px-5 z-20">
      {/* Mobile: hamburger */}
      <Button
        type="icon"
        intent="muted"
        size="sm"
        icon={<Menu />}
        onClick={onOpenMobileNav}
        aria-label={t('nav.open_nav')}
        className="md:hidden shrink-0"
      />

      {/* Left flex spacer — pushes search toward the middle */}
      <div className="flex-1" />

      {/* Global search trigger — opens command palette (not SearchInput: no inline query). */}
      <InputGroup size="md" className="w-full max-w-lg">
        <InputGroupAddon align="inline-start" className="pl-3">
          <Search className="h-4 w-4 text-dark-invirtus/40 pointer-events-none" aria-hidden />
        </InputGroupAddon>
        <InputGroupInput
          readOnly
          defaultValue=""
          rounded="full"
          startAddonPadding="icon"
          endAddonPadding="button"
          aria-label={t('nav.open_search')}
          className="cursor-pointer"
          onClick={openSearch}
          onFocus={(e) => {
            e.currentTarget.blur();
            openSearch();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openSearch();
            }
          }}
        />
        <InputGroupAddon align="inline-end" className="gap-0.5 pointer-events-none pr-3">
          <kbd className={kbdClass}>{isMac ? '⌘' : 'Ctrl'}</kbd>
          <kbd className={kbdClass}>K</kbd>
        </InputGroupAddon>
      </InputGroup>

      {/* Right flex spacer — symmetric with the left, keeps the search centered.
          The language globe sits at its trailing edge so it lines up with the
          right gutter without crowding the search input. */}
      <div className="flex-1 flex items-center justify-end">
        <LanguageGlobe variant="subtle" />
      </div>
    </header>
  );
};
